/**
 * The panel's left column: the project's run definitions in two groups,
 * Enabled (on the default branch) and Proposed (only in an open MR). Each
 * entry is named by its file stem, so a broken file is still listed, with
 * its lint badge. Entries are selected by `file_name`, which is unique
 * where the stem is not (`a.yaml` beside `a.yml`).
 */

import type { ReactElement } from 'react';
import { DefinitionBadge } from './DefinitionBadge';
import { isProjectStateFinding, splitFindings } from '../utils/policyFindings';
import type { DefinitionSummary } from '../types/projectPanel.types';

interface DefinitionListProps {
  definitions: DefinitionSummary[];
  proposed: DefinitionSummary[];
  /** More open MRs than the backend scans. */
  truncated: boolean;
  /** Open MRs whose changes could not be read. */
  unchecked: number[];
  defaultBranch: string | null;
  /** The selected entry's `file_name`. */
  selected: string | null;
  onSelect: (fileName: string) => void;
}

/**
 * From the definition's own findings, split as the detail splits them: the
 * project's state (harness not assigned, paused, harness inactive) is shown
 * once, by the setup status and pause above the list, never as each entry's
 * lint. A
 * definition's own ceiling breach (its agent or model) is `not allowed`.
 */
function LintBadge({ summary }: { summary: DefinitionSummary }): ReactElement | null {
  const { lint, policy } = splitFindings(summary.findings);
  if (lint.some((f) => f.severity === 'error')) return <DefinitionBadge kind="lint-error" />;
  if (policy.some((f) => f.severity === 'error' && !isProjectStateFinding(f))) {
    return <DefinitionBadge kind="policy-error" />;
  }
  if (lint.length > 0) return <DefinitionBadge kind="lint-warning" />;
  return null;
}

function enabledMeta(summary: DefinitionSummary): ReactElement {
  const run = summary.last_run;
  return (
    <>
      {summary.trigger.join(', ') || 'no trigger'} ·{' '}
      {run === null ? (
        'no runs yet'
      ) : (
        <>
          last run{' '}
          <span className={`def-item__dot def-item__dot--${run.status}`} aria-hidden="true" />
          {run.status}
        </>
      )}
    </>
  );
}

function proposedMeta(summary: DefinitionSummary): string {
  const mr = summary.pending_mr;
  const by = mr === null ? '' : `!${String(mr.iid)} · ${mr.author} · `;
  return `${by}not active yet`;
}

interface ItemProps {
  summary: DefinitionSummary;
  proposed: boolean;
  selected: boolean;
  onSelect: (fileName: string) => void;
}

function DefinitionItem({ summary, proposed, selected, onSelect }: ItemProps): ReactElement {
  const mr = summary.pending_mr;
  return (
    <li>
      <button
        type="button"
        className={`def-item${selected ? ' def-item--selected' : ''}`}
        aria-pressed={selected}
        onClick={() => { onSelect(summary.file_name); }}
      >
        <span className="def-item__head">
          <span className="def-item__name">{summary.name}</span>
          {proposed && <DefinitionBadge kind="proposed" />}
          {!proposed && mr !== null && (
            <DefinitionBadge kind={mr.change === 'deleted' ? 'removal' : 'edit'} mrIid={mr.iid} />
          )}
          <LintBadge summary={summary} />
        </span>
        <span className="def-item__meta">
          {proposed ? proposedMeta(summary) : enabledMeta(summary)}
        </span>
      </button>
    </li>
  );
}

interface GroupProps {
  title: string;
  empty: string;
  entries: DefinitionSummary[];
  proposed: boolean;
  selected: string | null;
  onSelect: (fileName: string) => void;
}

function DefinitionGroup({ title, empty, entries, proposed, selected, onSelect }: GroupProps): ReactElement {
  const id = `def-group-${proposed ? 'proposed' : 'enabled'}`;
  return (
    <section className="def-list__group" aria-labelledby={id}>
      <h3 id={id} className="def-list__title">{title}</h3>
      {entries.length === 0 ? (
        <p className="def-list__empty">{empty}</p>
      ) : (
        <ul className="def-list__items">
          {entries.map((s) => (
            <DefinitionItem
              key={s.file_name}
              summary={s}
              proposed={proposed}
              selected={s.file_name === selected}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function DefinitionList(props: DefinitionListProps): ReactElement {
  const { definitions, proposed, truncated, unchecked, defaultBranch, selected, onSelect } = props;
  const branch = defaultBranch ?? 'the default branch';
  return (
    <div className="def-list">
      <DefinitionGroup
        title={`Enabled (${defaultBranch ?? 'default branch'})`}
        empty={`No run definitions on ${branch}.`}
        entries={definitions}
        proposed={false}
        selected={selected}
        onSelect={onSelect}
      />
      <DefinitionGroup
        title="Proposed (not merged)"
        empty="No open MR proposes a definition."
        entries={proposed}
        proposed
        selected={selected}
        onSelect={onSelect}
      />
      {truncated && (
        <p className="def-list__note">Only the 50 most recently updated open MRs are checked.</p>
      )}
      {unchecked.length > 0 && (
        <p className="def-list__note">
          Some open MRs couldn&apos;t be checked ({unchecked.map((iid) => `!${String(iid)}`).join(', ')});
          a definition they change may be missing or out of date here.
        </p>
      )}
    </div>
  );
}
